import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import EcxSurveyEditor from '../../helpers/ecxsurvey/EcxSurveyEditor';
import { useSurveyStore } from '../../helpers/ecxsurvey/surveyutilities/surveyStore';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import surveyService from '../../../services/survey.service';
import SubHeader from '../../layout/components/SubHeader';
import Loading from '../../common/Loading';

function SurveyEditPage() {
    const { surveyId } = useParams();
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const navigate = useNavigate();
    const loadSurveyData = useSurveyStore((state) => state.loadSurveyData);
    const exportToJSON = useSurveyStore((state) => state.exportToJSON);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [title, setTitle] = useState('Edit survey');

    const loadSurvey = useCallback(() => {
        if (!clinicId || !surveyId) return;

        setLoading(true);
        setError(null);

        surveyService
            .getClinicSurveyById(clinicId, surveyId)
            .then((res) => {
                loadSurveyData({
                    survey: res.data.survey,
                    questions: res.data.questions || [],
                });
                setTitle(res.data.survey?.title || 'Edit survey');
            })
            .catch((err) => {
                console.error(err);
                setError('Failed to load survey. Please try again.');
            })
            .finally(() => setLoading(false));
    }, [clinicId, surveyId, loadSurveyData]);

    useEffect(() => {
        loadSurvey();
    }, [loadSurvey]);

    const persist = async (statusOverride) => {
        if (!clinicId || !surveyId || saving) return;

        const surveyData = exportToJSON();
        if (!Array.isArray(surveyData.questions) || surveyData.questions.length === 0) {
            toast.error('Add at least one question before saving the survey');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                survey: {
                    ...surveyData.survey,
                    ...(statusOverride ? { status: statusOverride } : {}),
                },
                questions: surveyData.questions || [],
            };
            await surveyService.updateClinicSurvey(clinicId, surveyId, payload);
            toast.success(statusOverride === 'ACTIVE' ? 'Survey activated' : 'Survey saved');
            if (payload.survey?.title) {
                setTitle(payload.survey.title);
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to save survey');
        } finally {
            setSaving(false);
        }
    };

    if (error) {
        return (
            <div className="ecx-page surveys-page ecx-page--full-width">
                <SubHeader
                    title="Edit survey"
                    description="Clinic survey editor"
                    icon="bi-clipboard-check"
                    containerClass="w-100 px-4"
                >
                    <Link to="/surveys" className="btn btn-sm btn-outline-secondary rounded-pill">
                        <i className="bi bi-chevron-left me-1" />
                        Back to surveys
                    </Link>
                </SubHeader>
                <div className="ecx-page-body">
                    <div className="alert alert-warning border-0 shadow-sm">{error}</div>
                    <button type="button" className="btn btn-sm btn-primary" onClick={loadSurvey}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="ecx-page surveys-page ecx-page--full-width">
            <SubHeader
                title={title}
                description="Edit clinic survey"
                icon="bi-clipboard-check"
                containerClass="w-100 px-4"
            >
                <div className="d-flex gap-2">
                    <Link to={`/surveys/${surveyId}/results`} className="btn btn-sm btn-outline-secondary rounded-pill">
                        <i className="bi bi-bar-chart me-1" />
                        Results
                    </Link>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill"
                        onClick={() => navigate('/surveys')}
                    >
                        <i className="bi bi-chevron-left me-1" />
                        Back
                    </button>
                </div>
            </SubHeader>
            <div className="ecx-page-body">
                {loading ? (
                    <Loading />
                ) : (
                    <EcxSurveyEditor onSave={() => persist()} onPublish={() => persist('ACTIVE')} />
                )}
            </div>
        </div>
    );
}

export default SurveyEditPage;
